import React, { useMemo } from 'react';
import type { OptionProps } from '@atlaskit/select';
import type { GroupBase, OptionsOrGroups } from '@atlaskit/react-select';
import { useIntl } from '@atlassian/jira-intl';
import { OptionWithButtonStyle } from '@atlassian/jira-select-with-footer-options/src/OptionWithButtonStyle.tsx';
import {
	appendStickyFooterOptions,
	type FooterOption,
} from '@atlassian/jira-select-with-footer-options/src/utils.tsx';
import { OptionWithAutoKeyboardScroll } from '@atlassian/jira-select-with-footer-options/src/OptionWithAutoKeyboardScroll.tsx';
import {
	CREATE_STATUS_OPTION_TYPE,
	EDIT_STATUS_OPTION_TYPE,
	MANAGE_WORKFLOW_OPTION_TYPE,
	type StatusTransitionOption,
} from '../../common/types.tsx';
import messages from './messages.tsx';

export const OptionComponent = ({
	children,
	...optionProps
}: OptionProps<StatusTransitionOption | FooterOption>): React.JSX.Element => {
	if (
		[CREATE_STATUS_OPTION_TYPE, EDIT_STATUS_OPTION_TYPE, MANAGE_WORKFLOW_OPTION_TYPE].includes(
			optionProps.data.type,
		)
	) {
		return <OptionWithButtonStyle {...optionProps}>{children}</OptionWithButtonStyle>;
	}
	return <OptionWithAutoKeyboardScroll {...optionProps}>{children}</OptionWithAutoKeyboardScroll>;
};

export const useSelectOptions = (
	targetOptions: StatusTransitionOption[],
	hasFooter?: boolean,
): OptionsOrGroups<
	StatusTransitionOption | FooterOption,
	GroupBase<StatusTransitionOption | FooterOption>
> => {
	const { formatMessage } = useIntl();
	const footerOptions: FooterOption[] = useMemo(() => {
		return [
			{
				label: formatMessage(messages.createStatus),
				type: CREATE_STATUS_OPTION_TYPE,
				value: CREATE_STATUS_OPTION_TYPE,
			},
			{
				label: formatMessage(messages.editStatus),
				type: EDIT_STATUS_OPTION_TYPE,
				value: EDIT_STATUS_OPTION_TYPE,
			},
			{
				label: formatMessage(messages.manageWorkflow),
				type: MANAGE_WORKFLOW_OPTION_TYPE,
				value: MANAGE_WORKFLOW_OPTION_TYPE,
			},
		];
	}, [formatMessage]);
	const optionsWithFooter = appendStickyFooterOptions<StatusTransitionOption>(
		targetOptions,
		footerOptions,
	);

	if (hasFooter) {
		return optionsWithFooter;
	}
	return targetOptions;
};
